import { type Flight, type LogbookEntry, orderEntriesChronologically } from '@/lib/db';

import { entryTotalHours } from './columns';

/**
 * The running "brought forward" total — `NZ-LOGBOOK.md` §3: brought forward,
 * this page, total to date, computed rather than typed.
 *
 * Only a *confirmed* entry adds to the total. A draft sits in the list with
 * whatever total already stood before it, because it has not been reviewed
 * and `PLAN.md` §2 is explicit that nothing the pilot hasn't looked at gets
 * silently counted. It still needs a number to show, though — an
 * unapproved row with a blank total column would read as a gap in the
 * arithmetic rather than what it is, a flight waiting on review.
 */
export interface RunningTotal {
  entryId: string;
  /** This entry's own flight time, whether or not it counts yet. */
  hours: number;
  /** Opening balance plus every confirmed entry up to and including this row. */
  total: number;
  counted: boolean;
}

/**
 * @param entries  any order; put into `compareEntriesChronologically` order
 *                 internally — the *same* order a logbook page and a printed
 *                 page render in, which is what makes row N's "to date" the
 *                 total as at row N rather than as at some other row.
 * @param flightById  passed straight through to `orderEntriesChronologically`
 *                     — omit it and callers get the same order as always.
 */
export function computeRunningTotals(
  entries: readonly LogbookEntry[],
  openingBalanceHr: number,
  flightById?: ReadonlyMap<string, Flight>,
): RunningTotal[] {
  const ordered = orderEntriesChronologically(entries, flightById);

  let running = openingBalanceHr;
  return ordered.map((entry) => {
    const hours = entryTotalHours(entry);
    const counted = entry.confirmedAt !== undefined;
    if (counted) {
      running += hours;
    }
    return { entryId: entry.id, hours, total: running, counted };
  });
}

/** The sum of every confirmed entry's flight time — what "total time to date" reads on its own, without the opening balance. */
export function confirmedHoursTotal(entries: readonly LogbookEntry[]): number {
  return entries
    .filter((entry) => entry.confirmedAt !== undefined)
    .reduce((sum, entry) => sum + entryTotalHours(entry), 0);
}
